import type { ChainInfo } from '../types';

export const config = {
  walletConnect: {
    projectId: (import.meta.env as any).VITE_WALLET_CONNECT_PROJECT_ID || (import.meta.env as any).NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID || '',
  },
  ink: {
    chainId: 57073,
    // Primary RPC (your private Gelato RPC Pro - use for all operations)
    primaryRpcUrl: (import.meta.env as any).VITE_GELATO_RPC_URL || 'https://rpc-gel.inkonchain.com', // Falls back to free public if not set
    // Websocket RPC for real-time subscriptions (if needed)
    wsRpcUrl: (import.meta.env as any).VITE_GELATO_WS_URL,
    // Fallback RPCs (free public endpoints as backup - only if Gelato Pro fails)
    fallbackRpcUrls: [
      'https://rpc-gel.inkonchain.com', // Gelato public RPC (works on localhost)
      'https://ink.rpc.grove.city/v1/01fdb492', // Grove public RPC
      'https://ink.drpc.org', // DRPC public RPC
    ],
    explorerUrl: (import.meta.env as any).VITE_INK_EXPLORER_URL || 'https://explorer.inkonchain.com',
    // Blockscout API Keys - Use up to 3 for 30 RPS combined (10 RPS each)
    blockscoutApiKeys: [
      (import.meta.env as any).VITE_BLOCKSCOUT_API_KEY_1 || (import.meta.env as any).VITE_BLOCKSCOUT_API_KEY || '',
      (import.meta.env as any).VITE_BLOCKSCOUT_API_KEY_2 || '',
      (import.meta.env as any).VITE_BLOCKSCOUT_API_KEY_3 || '',
    ].filter(key => key !== ''),
    tokenFactoryAddress: (import.meta.env as any).VITE_MAVRK_TOKEN_FACTORY_ADDRESS || '0xE01118dbcF8Ba2609d3ffBD6A53d9533c7526D60',
    mavrkLensAddress: (import.meta.env as any).VITE_MAVRK_LENS_ADDRESS || '0x89C17fEBb23d78802c85B541275a5689aec5852D',
    vestingContractAddress: (import.meta.env as any).VITE_MAVRK_VESTING_ADDRESS || '0x9496Ff7A7BE0A91F582Baa96ac12a0A36300750c',
    tokenLockerAddress: '0xd2C5947A6777500D9b7ad55a4D48AeF855AE6aBA',
    creatorCardNFTAddress: (import.meta.env as any).VITE_CREATOR_CARD_NFT_ADDRESS || '0xBc1504Bc9Aa17e94E30D41ef581c6b87A1208cB6',
    mavrkSwapRouterAddress: (import.meta.env as any).VITE_MAVRK_SWAP_ROUTER_ADDRESS || '0x255a501d300647134b8569Ff2772Fbdf5564a32b',
  },
  // API endpoints removed - all backend functionality now uses Supabase
  supabase: {
    url: (import.meta.env as any).VITE_SUPABASE_URL || 'https://esjrycmiokijtxnbfyox.supabase.co',
    anonKey: (import.meta.env as any).VITE_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImVzanJ5Y21pb2tpanR4bmJmeW94Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ2NDc3OTIsImV4cCI6MjA3MDIyMzc5Mn0.mBXxIiC0cWzPufhvS6TyvZTC9H4AcxN0ljRYD-caEVk',
  },
  gelato: {
    apiKey: (import.meta.env as any).VITE_GELATO_API_KEY || 'HAP3lYpA_nbFtzY2aczDGGqF6xRvx29i4ybGr_eIBA4_',
    trustedForwarder: '0x61F2976610970AFeDc1d83229e1E21bdc3D5cbE4', // Ink L2 trusted forwarder
    relayUrl: 'https://relay.gelato.digital',
    gasPrice: '0.0005', // 0.0005 Gwei - confirmed working for direct contract calls
  },
  ethereum: {
    chainId: 1,
    // Ethereum mainnet RPC URLs (can use public or Gelato RPC if available)
    rpcUrls: [
      (import.meta.env as any).VITE_ETH_RPC_URL || 'https://eth.llamarpc.com',
      'https://rpc.ankr.com/eth',
      'https://eth-mainnet.public.blastapi.io',
    ],
    explorerUrl: 'https://etherscan.io',
    // Gelato trusted forwarder for Ethereum mainnet (1Balance ERC2771 - non-concurrent)
    // This is for sponsoredCallERC2771 method (not concurrent)
    trustedForwarder: (import.meta.env as any).VITE_GELATO_ETH_TRUSTED_FORWARDER || '0xd8253782c45a12053594b9deB72d8e8aB2Fca54c',
    // V4 Factory address on Ethereum mainnet
    tokenFactoryAddress: '0x25CA68F2C3d6c26623670C93426d74C901068bD6',
  },
  platformWallet: {
    // Platform wallet for frictionless deployments (no user wallet required)
    privateKey: (import.meta.env as any).VITE_PLATFORM_WALLET_PRIVATE_KEY,
    address: (import.meta.env as any).VITE_PLATFORM_WALLET_ADDRESS,
  },
} as const;

// Validate required environment variables
export const validateConfig = () => {
  const required = [
    'VITE_WALLET_CONNECT_PROJECT_ID',
    'VITE_INK_EXPLORER_URL',
    'VITE_MAVRK_TOKEN_FACTORY_ADDRESS',
    // Optional: uncomment to enforce Noves
    // 'VITE_NOVES_API_KEY',
  ];

  const missing = required.filter(key => !(import.meta.env as any)[key]);
  
  if (missing.length > 0) {
    console.warn('Missing environment variables:', missing);
  }
}; 

export const DEFAULT_CHAIN = 'ink';

export const CHAINS: ChainInfo[] = [
  {
    key: 'ink',
    name: 'Ink',
    chainId: 57073,
    logoUrl: 'https://icons.llamao.fi/icons/chains/rsz_ink.jpg',
    explorerUrl: 'https://explorer.inkonchain.com',
    deployWriteUrl: 'https://explorer.inkonchain.com/address/0xeb8adefef252e1a0ccc4e1d7546f605f3061782c#writeContract',
    isDeployed: true,
  },
]; 
